import {
  deleteOutboxOperation,
  getMeta,
  getNote,
  getOutboxOperation,
  getOutboxOperations,
  isNativeDatabase,
  putNote,
  putOutboxOperation,
  setMeta,
} from "./db.js";
import {
  decryptNoteBody,
  deriveVaultId,
  encryptNoteBody,
  normalizeVaultKey,
  SyncEncryptionError,
} from "./crypto.js";
import { conflictCopyBody, conflictCopyId, mergeConcurrentText } from "./merge.js";

let inFlightSync = null;

const DEFAULT_ADAPTER = {
  deleteOutboxOperation,
  getMeta,
  getNote,
  getOutboxOperation,
  getOutboxOperations,
  isNativeDatabase,
  putNote,
  putOutboxOperation,
  setMeta,
  fetch: (...args) => fetch(...args),
};

export class SyncConfigurationError extends Error {
  constructor(message) {
    super(message);
    this.name = "SyncConfigurationError";
  }
}

export { SyncEncryptionError };

export function makeOperation(note, existing = null) {
  const noteRevision = note.serverRevision || 0;
  const preserveExistingBase = existing && existing.baseRevision >= noteRevision;
  return {
    opId: crypto.randomUUID(),
    noteId: note.id,
    baseRevision: preserveExistingBase ? existing.baseRevision : noteRevision,
    // baseBody never leaves the client. It enables a conservative 3-way merge.
    baseBody: preserveExistingBase
      ? (existing.baseBody ?? note.syncedBody ?? null)
      : (note.syncedBody ?? null),
    body: note.body,
    createdAt: note.createdAt,
    updatedAt: note.updatedAt,
    deletedAt: note.deletedAt,
    pinned: Boolean(note.pinned),
  };
}

export async function queueNote(note, adapter = DEFAULT_ADAPTER) {
  const existing = await adapter.getOutboxOperation(note.id);
  const operation = makeOperation(note, existing);
  await adapter.putOutboxOperation(operation);
  return operation;
}

async function serverNoteToLocal(note, vaultKey) {
  const body = await decryptNoteBody(note.body, note.id, vaultKey);
  return {
    id: note.id,
    body,
    createdAt: note.createdAt,
    updatedAt: note.updatedAt,
    deletedAt: note.deletedAt,
    pinned: Boolean(note.pinned),
    serverRevision: note.revision,
    syncedBody: body,
    sourceDeviceId: note.deviceId,
    sourceOpId: note.opId,
  };
}

async function operationToWire(operation, vaultKey) {
  // Keep baseBody local: spreading the operation here would leak the plaintext
  // merge base into an otherwise encrypted request.
  return {
    opId: operation.opId,
    noteId: operation.noteId,
    baseRevision: operation.baseRevision,
    body: await encryptNoteBody(operation.body, operation.noteId, vaultKey),
    createdAt: operation.createdAt,
    updatedAt: operation.updatedAt,
    deletedAt: operation.deletedAt,
    pinned: Boolean(operation.pinned),
  };
}

async function operationFromWire(operation, vaultKey) {
  return {
    ...operation,
    body: await decryptNoteBody(operation.body, operation.noteId, vaultKey),
  };
}

function noteAtRemoteRevision(local, remote, body = local.body) {
  return {
    ...local,
    body,
    serverRevision: remote.serverRevision,
    syncedBody: remote.body,
  };
}

function latestDeletionTimestamp(local, remote) {
  const timestamps = [local.deletedAt, remote.deletedAt].filter(
    (value) => Number.isFinite(value) && value > 0,
  );
  return timestamps.length ? Math.max(...timestamps) : null;
}

function sameSyncedNote(left, right) {
  return (
    left.body === right.body &&
    left.deletedAt === right.deletedAt &&
    Boolean(left.pinned) === Boolean(right.pinned)
  );
}

async function resolveDeletionRace({ adapter, local, pending, remote }) {
  // Deletion is a tombstone, not a text edit. If either side is deleted, an
  // older edit from another device must never resurrect the note merely
  // because both bodies happen to be identical. An explicit restore still
  // works after the device has first synced the tombstone and therefore writes
  // against its current server revision.
  const deletedAt = latestDeletionTimestamp(local, remote);
  if (!deletedAt) return null;

  const baseBody = pending?.baseBody ?? local.syncedBody ?? null;
  const merge = mergeConcurrentText(baseBody, local.body, remote.body);
  const body = merge.clean ? merge.text : local.body;
  const resolved = {
    ...noteAtRemoteRevision(local, remote, body),
    deletedAt,
    pinned: false,
    updatedAt: Math.max(local.updatedAt, remote.updatedAt, deletedAt),
  };

  if (sameSyncedNote(resolved, remote)) {
    if (pending) await adapter.deleteOutboxOperation(local.id, pending.opId);
    await adapter.putNote({ ...remote, pinned: false });
  } else {
    await adapter.putNote(resolved);
    await queueNote(resolved, adapter);
  }
  return "rebased";
}

async function savePendingAtRemoteRevision(adapter, local, remote, body = local.body) {
  const note = noteAtRemoteRevision(local, remote, body);
  await adapter.putNote(note);
  await queueNote(note, adapter);
  return note;
}

async function preserveTrueConflict({
  adapter,
  local,
  remote,
  localOpId,
  remoteOpId,
}) {
  // The note the user is editing remains the main note. Preserve the remote
  // version in one deterministic copy, then rebase the main note for retry.
  const copyId = await conflictCopyId(local.id, localOpId, remoteOpId);
  if (!(await adapter.getNote(copyId))) {
    const timestamp = Date.now();
    const copy = {
      id: copyId,
      body: conflictCopyBody(remote.body),
      createdAt: timestamp,
      updatedAt: timestamp,
      deletedAt: null,
      pinned: false,
      serverRevision: 0,
      syncedBody: null,
    };
    await adapter.putNote(copy);
    await queueNote(copy, adapter);
  }
  await savePendingAtRemoteRevision(adapter, local, remote);
}

async function resolveConcurrentVersion({
  adapter,
  deviceId,
  local,
  pending,
  remote,
  localOpId,
  remoteOpId,
}) {
  if (remote.sourceDeviceId === deviceId) {
    await savePendingAtRemoteRevision(adapter, local, remote);
    return "rebased";
  }

  const deletionOutcome = await resolveDeletionRace({ adapter, local, pending, remote });
  if (deletionOutcome) return deletionOutcome;

  const baseBody = pending?.baseBody ?? local.syncedBody ?? null;
  const merge = mergeConcurrentText(baseBody, local.body, remote.body);
  if (merge.clean) {
    if (merge.text === remote.body) {
      if (pending) await adapter.deleteOutboxOperation(local.id, pending.opId);
      await adapter.putNote({ ...remote });
    } else {
      const merged = {
        ...noteAtRemoteRevision(local, remote, merge.text),
        updatedAt: merge.text === local.body ? local.updatedAt : Date.now(),
      };
      await adapter.putNote(merged);
      await queueNote(merged, adapter);
    }
    return merge.reason === "local-only" ? "rebased" : "merged";
  }

  await preserveTrueConflict({ adapter, local, remote, localOpId, remoteOpId });
  return "conflict";
}

async function integrateRemoteChange({ adapter, accepted, deviceId, remote }) {
  const local = await adapter.getNote(remote.id);
  if (!local) {
    await adapter.putNote({ ...remote });
    return null;
  }
  if ((local.serverRevision || 0) >= remote.serverRevision) return null;

  const pending = await adapter.getOutboxOperation(remote.id);
  if (!pending) {
    await adapter.putNote({ ...remote });
    return null;
  }

  if (accepted.has(remote.sourceOpId)) {
    if (pending.opId === remote.sourceOpId) {
      await adapter.deleteOutboxOperation(remote.id, pending.opId);
      await adapter.putNote({ ...remote });
      return null;
    }
    const rebased = noteAtRemoteRevision(local, remote);
    await adapter.putNote(rebased);
    pending.baseRevision = remote.serverRevision;
    pending.baseBody = remote.body;
    await adapter.putOutboxOperation(pending);
    return "rebased";
  }

  return resolveConcurrentVersion({
    adapter,
    deviceId,
    local,
    pending,
    remote,
    localOpId: pending.opId,
    remoteOpId: remote.sourceOpId,
  });
}

async function resolveRejectedOperation({
  adapter,
  conflict,
  deviceId,
  sentOperation,
  vaultKey,
}) {
  const attempted = sentOperation || (await operationFromWire(conflict.clientOperation, vaultKey));
  const remote = conflict.serverNote
    ? await serverNoteToLocal(conflict.serverNote, vaultKey)
    : null;
  if (!remote) return "conflict";

  const currentPending = await adapter.getOutboxOperation(attempted.noteId);
  const storedLocal = await adapter.getNote(attempted.noteId);
  const local = storedLocal || {
    id: attempted.noteId,
    body: attempted.body,
    createdAt: attempted.createdAt,
    updatedAt: attempted.updatedAt,
    deletedAt: attempted.deletedAt,
    pinned: Boolean(attempted.pinned),
    serverRevision: attempted.baseRevision,
    syncedBody: attempted.baseBody ?? null,
  };

  if (currentPending?.opId === attempted.opId) {
    await adapter.deleteOutboxOperation(attempted.noteId, attempted.opId);
  }

  const pendingForMerge = currentPending || attempted;
  if (pendingForMerge.baseBody == null && sentOperation?.baseBody != null) {
    pendingForMerge.baseBody = sentOperation.baseBody;
  }

  return resolveConcurrentVersion({
    adapter,
    deviceId,
    local,
    pending: pendingForMerge,
    remote,
    localOpId: pendingForMerge.opId,
    remoteOpId: remote.sourceOpId,
  });
}

export async function applySync(deviceId, adapter = DEFAULT_ADAPTER) {
  const [cursor, operations, configuredUrl, configuredToken, configuredVaultKey] = await Promise.all([
    adapter.getMeta("syncCursor", 0),
    adapter.getOutboxOperations(),
    adapter.getMeta("serverUrl", ""),
    adapter.getMeta("serverToken", ""),
    adapter.getMeta("vaultKey", ""),
  ]);

  if (adapter.isNativeDatabase() && !configuredUrl) {
    throw new SyncConfigurationError("尚未设置云端同步地址");
  }
  if (!configuredVaultKey) {
    throw new SyncConfigurationError("尚未设置端到端加密密钥");
  }

  const serverUrl = configuredUrl ? configuredUrl.replace(/\/+$/, "") : "";
  const vaultKey = normalizeVaultKey(configuredVaultKey);
  const [vaultId, wireOperations] = await Promise.all([
    deriveVaultId(vaultKey),
    Promise.all(operations.map((operation) => operationToWire(operation, vaultKey))),
  ]);
  const headers = { "Content-Type": "application/json" };
  if (configuredToken) headers.Authorization = `Bearer ${configuredToken}`;

  const response = await adapter.fetch(`${serverUrl}/api/sync`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      protocolVersion: 1,
      vaultId,
      deviceId,
      cursor,
      operations: wireOperations,
    }),
  });

  if (!response.ok) {
    const detail = await response.json().catch(() => ({}));
    if (detail.code === "vault_key_mismatch") {
      throw new SyncEncryptionError(detail.error, detail.code);
    }
    throw new Error(detail.error || `Sync failed (${response.status})`);
  }

  const result = await response.json();
  const accepted = new Set(result.acceptedOpIds || []);
  const sentByOpId = new Map(operations.map((operation) => [operation.opId, operation]));
  const conflictedNoteIds = new Set(
    (result.conflicts || []).map((conflict) => conflict.clientOperation.noteId),
  );
  const stats = { rebased: 0, merged: 0, conflicts: 0 };

  for (const conflict of result.conflicts || []) {
    const outcome = await resolveRejectedOperation({
      adapter,
      conflict,
      deviceId,
      sentOperation: sentByOpId.get(conflict.clientOperation.opId),
      vaultKey,
    });
    if (outcome === "rebased") stats.rebased += 1;
    if (outcome === "merged") stats.merged += 1;
    if (outcome === "conflict") stats.conflicts += 1;
  }

  for (const change of result.changes || []) {
    if (conflictedNoteIds.has(change.id)) continue;
    const remote = await serverNoteToLocal(change, vaultKey);
    const outcome = await integrateRemoteChange({ adapter, accepted, deviceId, remote });
    if (outcome === "rebased") stats.rebased += 1;
    if (outcome === "merged") stats.merged += 1;
    if (outcome === "conflict") stats.conflicts += 1;
  }

  // Accepted operations normally appear in changes. This final conditional
  // delete also handles a very large, paginated change backlog safely.
  for (const operation of operations) {
    if (accepted.has(operation.opId)) {
      await adapter.deleteOutboxOperation(operation.noteId, operation.opId);
    }
  }

  await adapter.setMeta("syncCursor", result.cursor ?? cursor);

  return {
    pushed: accepted.size,
    pulled: (result.changes || []).length,
    conflicts: stats.conflicts,
    merged: stats.merged,
    rebased: stats.rebased,
    pending: (await adapter.getOutboxOperations()).length,
  };
}

export function synchronize(deviceId) {
  if (!inFlightSync) {
    inFlightSync = applySync(deviceId).finally(() => {
      inFlightSync = null;
    });
  }
  return inFlightSync;
}
